import { Editor, Frame } from '@craftjs/core';
import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';

import { BlockEditorProvider } from '@/components/blockEditor/BlockEditor.context';
import { EDUCATION_SLIDE_RESOLVER } from '@/components/blockEditor/presentation/educationSlideResolver';
import { transformCommonDataToCraft } from '@/components/blockEditor/transformCommonDataToCraft';
import { InteractionParadigmContext } from '@/contexts/InteractionParadigm.context';
import { CustomerThemeProvider } from '@/styles/themes/CustomerThemeProvider';
import { COLORS } from '@/styles/tokens/colors';

import { EducationSlideContentWrapper } from '../presentation/clientPresentation/components/slides/EducationalSlide/EducationalSlideWrapper';
import { PresentationSlideWrapper } from '../presentation/clientPresentation/PresentationSlideWrapper';
import {
  PresentationSlide,
  PresentationSlideProps,
} from '../presentation/PresentationSlide';
import { EducationListItemType } from './Education.constants';
import {
  useCustomSlide,
  useEducationMap,
  useEducationSlideOverride,
  useEducationSlideOverrides,
} from './EducationSlideOverrides/EducationSlideOverrides.context';
import { getEducationSlideDefaultData } from './EducationSlideTrowser/EducationSlideDefaultData';
import { getSlideIdentifierFromEducationSlideId } from './EducationSlideTrowser/utils/slideIdentifier.utils';

export interface EducationSlideDisplayProps {
  activeSlide: EducationListItemType | null;
  customSlideIdentifier?: string | null;
}

export function EducationSlideDisplay({
  activeSlide,
  customSlideIdentifier,
}: EducationSlideDisplayProps) {
  // If it's a custom slide, render CustomEducationSlideDisplay
  if (customSlideIdentifier) {
    return (
      <CustomEducationSlideDisplay slideIdentifier={customSlideIdentifier} />
    );
  }

  // If no activeSlide, don't render anything
  if (!activeSlide) {
    return null;
  }

  return <EducationSlideDisplayContent activeSlide={activeSlide} />;
}

interface EducationSlideDisplayContentProps {
  activeSlide: EducationListItemType;
}

function EducationSlideDisplayContent({
  activeSlide,
}: EducationSlideDisplayContentProps) {
  const educationMap = useEducationMap();
  const { slides, title } = educationMap[activeSlide];

  return (
    <CustomerThemeProvider>
      <Box
        flex={1}
        sx={{
          overflowY: 'scroll',
          backgroundColor: COLORS.GRAY[50],
          position: 'relative',
        }}
        data-testid="client-presentation-designer-v2-slide-display"
      >
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="flex-start"
          sx={{
            py: 2,
          }}
        >
          <InteractionParadigmContext.Provider value={{ viewOnly: true }}>
            {slides.map((Slide, idx) => (
              <PresentationSlideWrapper key={idx}>
                <StandaloneEducationSlide
                  id={`${activeSlide}-${idx}`}
                  slideId={activeSlide}
                  slideIndex={idx}
                  title={title}
                  Slide={Slide}
                  hasMorePages={idx < slides.length - 1}
                />
              </PresentationSlideWrapper>
            ))}
          </InteractionParadigmContext.Provider>
        </Stack>
      </Box>
    </CustomerThemeProvider>
  );
}

interface CustomEducationSlideDisplayProps {
  slideIdentifier: string;
}

function CustomEducationSlideDisplay({
  slideIdentifier,
}: CustomEducationSlideDisplayProps) {
  const customSlide = useCustomSlide(slideIdentifier);

  const commonData = customSlide?.contentJSON;

  const craftData = useMemo(() => {
    if (!commonData?.blocks || !Array.isArray(commonData.blocks)) {
      return null;
    }

    return transformCommonDataToCraft(
      commonData,
      'ROOT',
      'SlideRoot',
      {
        slideId: slideIdentifier,
        slideIndex: 0,
      },
      true // useLinkedNodesForRootChildren
    );
  }, [commonData, slideIdentifier]);

  if (!customSlide || !craftData) {
    return null;
  }

  const title = customSlide.displayName || 'Custom page';

  return (
    <CustomerThemeProvider>
      <Box
        flex={1}
        sx={{
          overflowY: 'scroll',
          backgroundColor: COLORS.GRAY[50],
          position: 'relative',
        }}
        data-testid="client-presentation-designer-v2-slide-display"
      >
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="flex-start"
          sx={{
            py: 2,
          }}
        >
          <InteractionParadigmContext.Provider value={{ viewOnly: true }}>
            <PresentationSlideWrapper>
              <Editor resolver={EDUCATION_SLIDE_RESOLVER} enabled={false}>
                <PresentationSlide.Slide
                  id={slideIdentifier}
                  leftHeaderContent={
                    <PresentationSlide.MainHeading heading={title} />
                  }
                  footer={<PresentationSlide.Footer />}
                >
                  <EducationSlideContentWrapper hasMorePages={false}>
                    <BlockEditorProvider isEditMode={false} rootNodeId="ROOT">
                      <Frame data={craftData} />
                    </BlockEditorProvider>
                  </EducationSlideContentWrapper>
                </PresentationSlide.Slide>
              </Editor>
            </PresentationSlideWrapper>
          </InteractionParadigmContext.Provider>
        </Stack>
      </Box>
    </CustomerThemeProvider>
  );
}

interface StandaloneEducationSlideProps {
  hasMorePages?: boolean;
  id: string;
  slideId: EducationListItemType;
  slideIndex: number;
  title: string;
  Slide: React.ElementType;
  /**
   * The shell around the slide content. Pass a different component to show
   * the content without the slide header, footer, and frame.
   */
  SlideShell?: React.ComponentType<PresentationSlideProps['Slide']>;
}
export function StandaloneEducationSlide({
  hasMorePages,
  Slide,
  title,
  id,
  slideId,
  slideIndex,
  SlideShell = PresentationSlide.Slide,
}: StandaloneEducationSlideProps) {
  // Generate the slide identifier for looking up overrides
  const slideIdentifier = getSlideIdentifierFromEducationSlideId(
    slideId,
    slideIndex
  );

  // Check if there's a tenant override for this slide
  const override = useEducationSlideOverride(slideIdentifier);
  const { loading: overridesLoading, error: overridesError } =
    useEducationSlideOverrides();

  const defaultData = useMemo(
    () => getEducationSlideDefaultData(slideId, slideIndex),
    [slideId, slideIndex]
  );

  // Use override contentJSON if available, otherwise use default data
  const commonData = useMemo(() => {
    // If there's an override, use it
    if (override?.contentJSON) {
      return override.contentJSON;
    }
    // Otherwise use the default data
    return defaultData;
  }, [override, defaultData]);

  const craftData = useMemo(
    () =>
      commonData
        ? transformCommonDataToCraft(
            commonData,
            'ROOT',
            'SlideRoot',
            {
              slideId,
              slideIndex,
            },
            true // useLinkedNodesForRootChildren
          )
        : null,
    [commonData, slideId, slideIndex]
  );

  // Craft reads Frame data only when it mounts. Wait until the effective
  // override query resolves so bundled content cannot become permanently stale.
  if (overridesLoading || overridesError) return null;

  return (
    <Editor resolver={EDUCATION_SLIDE_RESOLVER} enabled={false}>
      <SlideShell
        id={id}
        leftHeaderContent={<PresentationSlide.MainHeading heading={title} />}
        footer={<PresentationSlide.Footer />}
      >
        <EducationSlideContentWrapper hasMorePages={hasMorePages}>
          <BlockEditorProvider isEditMode={false} rootNodeId="ROOT">
            {craftData ? <Frame data={craftData} /> : <Slide />}
          </BlockEditorProvider>
        </EducationSlideContentWrapper>
      </SlideShell>
    </Editor>
  );
}
